import React, { memo, useEffect, useMemo, useRef } from 'react'
import { Box, useTheme } from '@mui/material'
import {
    ColorType,
    createChart,
    CrosshairMode,
    IChartApi,
    LineData,
    LineStyle,
    Time,
    UTCTimestamp,
} from 'lightweight-charts'
import merge from 'lodash/merge'
import { CHART_PRICE_SCALE_MARGINS, chartLayoutOptions } from '../../assets/constants'

const SECONDS_PER_BAR = 86400

const BASE_VALUES: readonly number[] = [
    23403, 23391, 23398, 23396, 23400, 23389, 23408, 23402, 23391, 23388,
    23376, 23375, 23377, 23362, 23384, 23387, 23378, 23368, 23356, 23363,
    23373, 23378, 23370, 23367, 23356, 23348, 23342, 23339, 23332, 23325,
    23330, 23319, 23340, 23347, 23360, 23357, 23361, 23344, 23350, 23356,
    23360, 23375, 23382, 23385, 23384, 23375, 23383, 23384, 23379, 23372,
    23376, 23414, 23431, 23438, 23429, 23427, 23417, 23408, 23425, 23441,
]

const buildLineData = (baseTime: number, offset: number): LineData<Time>[] =>
    BASE_VALUES.map((value, i) => ({
        time: (baseTime + i * SECONDS_PER_BAR) as UTCTimestamp,
        value: value + offset,
    }))

/** Multi-strike skeleton: several thin overlay lines + underlying, not a single area series. */
const MultiStrikeChartSkeletonPlaceholder = () => {
    const theme = useTheme()
    const containerRef = useRef<HTMLDivElement>(null)
    const chartRef = useRef<IChartApi | null>(null)

    const isDark = theme.palette.mode === 'dark'

    const lineProfiles = useMemo(() => {
        const strong = isDark ? 'rgba(255, 255, 255, 0.32)' : 'rgba(0, 0, 0, 0.28)'
        const medium = isDark ? 'rgba(255, 255, 255, 0.22)' : 'rgba(0, 0, 0, 0.18)'
        const light = isDark ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.12)'

        return [
            { offset: 0, color: strong, width: 2, style: LineStyle.Solid },
            { offset: 95, color: medium, width: 1, style: LineStyle.Solid },
            { offset: -75, color: medium, width: 1, style: LineStyle.Solid },
            { offset: 160, color: light, width: 1, style: LineStyle.Dashed },
            { offset: -140, color: light, width: 1, style: LineStyle.Dashed },
            { offset: 220, color: light, width: 1, style: LineStyle.Dotted },
        ]
    }, [isDark])

    const scaleTextColor = useMemo(
        () => (isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)'),
        [isDark]
    )

    const chartOptions = useMemo(() => {
        const gridColor = '#88888815'
        const scaleBorder = theme.palette.divider

        return merge({}, chartLayoutOptions, {
            layout: {
                background: {
                    type: ColorType.Solid,
                    color: theme.palette.background.paper,
                },
                fontFamily: `'Work Sans', sans-serif`,
                textColor: scaleTextColor,
                fontSize: 12,
            },
            grid: {
                horzLines: { visible: true, color: gridColor },
                vertLines: { visible: true, color: gridColor },
            },
            timeScale: {
                visible: true,
                borderColor: scaleBorder,
                borderVisible: true,
                ticksVisible: true,
                timeVisible: false,
            },
            rightPriceScale: {
                visible: true,
                borderColor: scaleBorder,
                borderVisible: true,
                alignLabels: true,
                ticksVisible: true,
                textColor: scaleTextColor,
                scaleMargins: CHART_PRICE_SCALE_MARGINS,
            },
            leftPriceScale: {
                visible: true,
                borderColor: scaleBorder,
                borderVisible: true,
                alignLabels: true,
                ticksVisible: true,
                textColor: scaleTextColor,
                scaleMargins: CHART_PRICE_SCALE_MARGINS,
            },
            crosshair: {
                mode: CrosshairMode.Hidden,
                vertLine: { visible: false },
                horzLine: { visible: false },
            },
            handleScroll: false,
            handleScale: false,
        })
    }, [theme.palette.background.paper, theme.palette.divider, scaleTextColor])

    const baseTime = useMemo(
        () => Math.floor(Date.now() / 1000) - BASE_VALUES.length * SECONDS_PER_BAR,
        []
    )

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const chart = createChart(container, { ...chartOptions, autoSize: true })

        lineProfiles.forEach((profile) => {
            const series = chart.addLineSeries({
                color: profile.color,
                lineWidth: profile.width as 1 | 2 | 3 | 4,
                lineStyle: profile.style,
                priceLineVisible: false,
                lastValueVisible: false,
                crosshairMarkerVisible: false,
            })
            series.priceScale().applyOptions({ scaleMargins: CHART_PRICE_SCALE_MARGINS })
            series.setData(buildLineData(baseTime, profile.offset))
        })

        chart.timeScale().fitContent()
        chartRef.current = chart

        return () => {
            chart.remove()
            chartRef.current = null
        }
    }, [chartOptions, baseTime, lineProfiles])

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                bgcolor: 'background.paper',
                overflow: 'hidden',
                pointerEvents: 'none',
            }}
        />
    )
}

export default memo(MultiStrikeChartSkeletonPlaceholder)
